import React, { Fragment, useState, useEffect } from 'react';
import ColumnSelector from '../components/ColumnSelector';
import useColumnVisibility from '../hooks/useColumnVisibility';
import {
  FolderOpen,
  Plus,
  Search,
  Edit2,
  Trash2,
  Loader2,
  X,
  Download,
  Paperclip,
  FileText,
  AlertCircle,
  CheckCircle,
  Building2,
  Layers,
  History,
} from 'lucide-react';
import { externalDocumentsApi, sectorsApi } from '../api';
import { toast } from 'sonner';
import { extractApiError, validateDateField, formatDateBR } from '../utils/dateValidation';

const STORAGE_LABELS = {
  year: 'Por Ano',
  certificate: 'Por Certificado',
};

const RETENTION_LABELS = {
  annual: 'Anual',
  biennial: 'Bienal',
};

const DISPOSAL_LABELS = {
  annual: 'Anual',
  monthly: 'Mensal',
  semiannual: 'Semestral',
  quarterly: 'Trimestral',
  valid: 'Vigente',
};

const ExternalDocuments = () => {
  const [groups, setGroups] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [stats, setStats] = useState({ total: 0, expired: 0, due_soon: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expiredOnly, setExpiredOnly] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [showDocModal, setShowDocModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [versionsDoc, setVersionsDoc] = useState(null);
  const [versionsList, setVersionsList] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [downloadingVersionId, setDownloadingVersionId] = useState(null);

  useEffect(() => {
    loadGroups();
    loadSectors();
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [selectedGroupId, expiredOnly]);

  const loadGroups = async () => {
    try {
      const res = await externalDocumentsApi.listGroups();
      setGroups(res.data || []);
      if (res.data?.length > 0 && !selectedGroupId) {
        setSelectedGroupId(res.data[0].id);
      }
    } catch (error) {
      toast.error('Erro ao carregar grupos');
    }
  };

  const loadSectors = async () => {
    try {
      const res = await sectorsApi.listActive();
      setSectors(res.data || []);
    } catch (error) {
      console.error('Erro ao carregar setores:', error);
    }
  };

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const params = { expired_only: expiredOnly || undefined };
      if (selectedGroupId) params.group_id = selectedGroupId;
      const [docsRes, statsRes] = await Promise.all([
        externalDocumentsApi.list(params),
        externalDocumentsApi.stats(),
      ]);
      setDocuments(docsRes.data || []);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar documentos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async (data) => {
    try {
      await externalDocumentsApi.createGroup(data);
      toast.success('Grupo criado com sucesso');
      setShowGroupModal(false);
      await loadGroups();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao criar grupo'));
    }
  };

  const handleUpdateGroup = async (data) => {
    try {
      await externalDocumentsApi.updateGroup(editingGroup.id, data);
      toast.success('Grupo atualizado com sucesso');
      setShowGroupModal(false);
      setEditingGroup(null);
      await loadGroups();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao atualizar grupo'));
    }
  };

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`Excluir o grupo "${group.name}"? Os documentos deste grupo devem ser excluídos antes de excluir o grupo.`)) return;
    try {
      await externalDocumentsApi.deleteGroup(group.id);
      toast.success('Grupo excluído');
      setSelectedGroupId('');
      await loadGroups();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao excluir grupo'));
    }
  };

  const handleCreateDocument = async (data) => {
    try {
      await externalDocumentsApi.create({ ...data, group_id: selectedGroupId });
      toast.success('Documento criado com sucesso');
      setShowDocModal(false);
      loadDocuments();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao criar documento'));
    }
  };

  const handleUpdateDocument = async (data) => {
    try {
      await externalDocumentsApi.update(editingDoc.id, data);
      toast.success('Documento atualizado com sucesso');
      setShowDocModal(false);
      setEditingDoc(null);
      loadDocuments();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao atualizar documento'));
    }
  };

  const handleDeleteDocument = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este documento?')) return;
    try {
      await externalDocumentsApi.delete(id);
      toast.success('Documento excluído');
      loadDocuments();
    } catch (error) {
      toast.error('Erro ao excluir documento');
    }
  };

  const handleDownloadFile = async (doc) => {
    if (!doc.has_file) return;
    setDownloadingId(doc.id);
    try {
      const res = await externalDocumentsApi.downloadFile(doc.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_name || `documento_${doc.name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao baixar arquivo'));
    } finally {
      setDownloadingId(null);
    }
  };

  const handleUploadFile = async (doc, file) => {
    if (!file) return;
    setUploadingId(doc.id);
    try {
      await externalDocumentsApi.uploadFile(doc.id, file);
      toast.success('Arquivo anexado com sucesso');
      loadDocuments();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao anexar arquivo'));
    } finally {
      setUploadingId(null);
    }
  };

  const handleDeleteFile = async (doc) => {
    if (!window.confirm('Remover o arquivo atual do documento? A versão anterior, se houver, voltará a ser a vigente.')) return;
    setUploadingId(doc.id);
    try {
      const res = await externalDocumentsApi.deleteFile(doc.id);
      toast.success(res.data?.message || 'Arquivo removido');
      loadDocuments();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao remover arquivo'));
    } finally {
      setUploadingId(null);
    }
  };

  const handleOpenVersions = async (doc) => {
    setVersionsDoc(doc);
    setVersionsList([]);
    setVersionsLoading(true);
    try {
      const res = await externalDocumentsApi.getVersions(doc.id);
      setVersionsList(res.data || []);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao carregar versões'));
    } finally {
      setVersionsLoading(false);
    }
  };

  const handleDownloadVersion = async (version) => {
    setDownloadingVersionId(version.id);
    try {
      const res = await externalDocumentsApi.downloadVersion(version.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', version.file_name || `documento_rev${version.version}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao baixar versão'));
    } finally {
      setDownloadingVersionId(null);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes == null) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const filtered = searchTerm
    ? documents.filter((d) =>
        [d.name, d.edition, d.area_name, ...(d.access_area_names || [])]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : documents;

  const COLUMNS = [
    { key: 'name', label: 'Nome', locked: true, render: (doc) => (
      <td className="text-white font-medium">
        <div className="flex items-center gap-2">
          <FileText size={16} className="text-green-500 flex-shrink-0" />
          <span
            title={
              doc.access_area_names?.length > 0
                ? `Áreas com acesso: ${doc.access_area_names.join(', ')}`
                : 'Acesso: todos os setores'
            }
          >
            {doc.name}
          </span>
        </div>
      </td>
    ) },
    { key: 'edition', label: 'Edição', render: (doc) => <td className="text-gray-400">{doc.edition || '-'}</td> },
    { key: 'version', label: 'Versão', render: (doc) => (
      <td className="text-gray-400">
        {doc.version && doc.version > 0 ? (
          <span className="inline-flex items-center gap-1">
            <span className="px-1.5 py-0.5 rounded text-[10px] bg-green-500/20 text-green-400 font-medium">
              Rev. {doc.version}
            </span>
            {doc.versions_count > 1 && (
              <span className="text-xs text-gray-500">{doc.versions_count}</span>
            )}
          </span>
        ) : '-'}
      </td>
    ) },
    { key: 'area', label: 'Área', render: (doc) => (
      <td className="text-gray-400">
        {doc.area_name ? (
          <span className="flex items-center gap-1">
            <Building2 size={13} className="text-gray-500" />
            {doc.area_name}
          </span>
        ) : '-'}
      </td>
    ) },
    { key: 'storage', label: 'Armazenamento', render: (doc) => <td className="text-gray-400">{STORAGE_LABELS[doc.storage_type] || doc.storage_type || '-'}</td> },
    { key: 'retention', label: 'Retenção', render: (doc) => <td className="text-gray-400">{RETENTION_LABELS[doc.retention_min] || doc.retention_min || '-'}</td> },
    { key: 'disposal', label: 'Disposição', render: (doc) => <td className="text-gray-400">{DISPOSAL_LABELS[doc.disposal] || doc.disposal || '-'}</td> },
    { key: 'expiration', label: 'Vencimento', render: (doc) => <td className="text-gray-400">{formatDateBR(doc.expiration_date)}</td> },
    { key: 'days_to_expire', label: 'Dias p/ Vencer', render: (doc) => (
      <td>
        {doc.days_to_expire == null ? (
          <span className="text-gray-500">-</span>
        ) : doc.days_to_expire < 0 ? (
          <span className="text-red-400 font-medium">
            Vencido há {Math.abs(doc.days_to_expire)} {Math.abs(doc.days_to_expire) === 1 ? 'dia' : 'dias'}
          </span>
        ) : doc.days_to_expire <= 30 ? (
          <span className="text-yellow-400 font-medium">
            {doc.days_to_expire} {doc.days_to_expire === 1 ? 'dia' : 'dias'}
          </span>
        ) : (
          <span className="text-green-400 font-medium">
            {doc.days_to_expire} {doc.days_to_expire === 1 ? 'dia' : 'dias'}
          </span>
        )}
      </td>
    ) },
    { key: 'status', label: 'Situação', render: (doc) => (
      <td>
        {doc.is_expired ? (
          <span className="px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400 flex items-center gap-1 w-fit">
            <AlertCircle size={12} />
            Vencido
          </span>
        ) : (
          <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400 flex items-center gap-1 w-fit">
            <CheckCircle size={12} />
            Vigente
          </span>
        )}
      </td>
    ) },
    { key: 'attachment', label: 'Anexo', render: (doc) => (
      <td>
        <div className="flex items-center gap-2">
          {doc.has_file ? (
            <>
              <button
                onClick={() => handleDownloadFile(doc)}
                className="flex items-center gap-1 text-blue-400 hover:text-blue-300"
                title="Baixar anexo atual"
              >
                {downloadingId === doc.id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              </button>
              <button
                onClick={() => handleOpenVersions(doc)}
                className="text-gray-400 hover:text-green-400"
                title="Histórico de versões"
              >
                <History size={14} />
              </button>
              <label
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-white cursor-pointer"
                title="Anexar nova versão (a anterior fica obsoleta)"
              >
                {uploadingId === doc.id ? <Loader2 size={12} className="animate-spin" /> : <Paperclip size={12} />}
                Novo
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.txt"
                  style={{ display: 'none' }}
                  disabled={uploadingId === doc.id}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (f) handleUploadFile(doc, f);
                  }}
                />
              </label>
              <button
                onClick={() => handleDeleteFile(doc)}
                disabled={uploadingId === doc.id}
                className="text-red-400 hover:text-red-300"
                title="Remover anexo"
              >
                {uploadingId === doc.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
              </button>
            </>
          ) : (
            <label
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-white cursor-pointer"
              title="Anexar arquivo"
            >
              {uploadingId === doc.id ? <Loader2 size={12} className="animate-spin" /> : <Paperclip size={12} />}
              Anexar
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.txt"
                style={{ display: 'none' }}
                disabled={uploadingId === doc.id}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) handleUploadFile(doc, f);
                }}
              />
            </label>
          )}
        </div>
      </td>
    ) },
    { key: 'actions', label: 'Ações', locked: true, headerClass: 'text-right', render: (doc) => (
      <td>
        <div className="flex gap-2 justify-end">
          <button
            onClick={() => { setEditingDoc(doc); setShowDocModal(true); }}
            className="p-1 text-gray-400 hover:text-blue-400"
          >
            <Edit2 size={18} />
          </button>
          <button onClick={() => handleDeleteDocument(doc.id)} className="p-1 text-gray-400 hover:text-red-400">
            <Trash2 size={18} />
          </button>
        </div>
      </td>
    ) },
  ];

  const { visibleColumns, visibleKeys, toggleColumn, resetColumns } = useColumnVisibility({ storageKey: 'external-documents', columns: COLUMNS });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <FolderOpen className="text-green-500" />
            Gestão de Documentos Externos
          </h1>
          <p className="text-gray-400 mt-1">Controle de documentos externos e seus vencimentos</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setEditingGroup(null); setShowGroupModal(true); }} className="btn-secondary flex items-center gap-2">
            <Plus size={20} />
            Novo Grupo
          </button>
          <button onClick={() => { setEditingDoc(null); setShowDocModal(true); }} className="btn-primary flex items-center gap-2" disabled={!selectedGroupId}>
            <Plus size={20} />
            Novo Documento
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Total de Documentos</p>
          <p className="text-2xl font-bold text-white">{stats.total}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Vencidos</p>
          <p className="text-2xl font-bold text-red-400">{stats.expired}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Vencem neste mês</p>
          <p className="text-2xl font-bold text-yellow-400">{stats.due_soon}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Grupos</p>
          <p className="text-2xl font-bold text-white">{groups.length}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Groups sidebar */}
        <div className="card overflow-hidden lg:col-span-1">
          <div className="p-3 border-b border-gray-700 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-300 flex items-center gap-2">
              <Layers size={16} className="text-green-500" />
              Grupos
            </span>
          </div>
          <div className="max-h-[480px] overflow-y-auto">
            {groups.length === 0 ? (
              <p className="p-4 text-sm text-gray-500 text-center">Nenhum grupo criado</p>
            ) : (
              groups.map((group) => (
                <div
                  key={group.id}
                  onClick={() => setSelectedGroupId(group.id)}
                  className={`flex items-center justify-between px-3 py-3 cursor-pointer border-b border-gray-800 transition-colors ${
                    selectedGroupId === group.id ? 'bg-green-500/10 border-l-2 border-l-green-500' : 'hover:bg-gray-800/40'
                  }`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-white truncate">{group.name}</p>
                    {group.description && (
                      <p className="text-xs text-gray-500 truncate">{group.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-gray-700 text-gray-300 mr-1">
                      {group.document_count ?? 0}
                    </span>
                    <button
                      onClick={() => { setEditingGroup(group); setShowGroupModal(true); }}
                      className="p-1 text-gray-400 hover:text-blue-400"
                      title="Editar grupo"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => handleDeleteGroup(group)}
                      className="p-1 text-gray-400 hover:text-red-400"
                      title="Excluir grupo"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Documents table */}
        <div className="card overflow-hidden lg:col-span-3">
          <div className="p-3 border-b border-gray-700 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Buscar documento, edição, área..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10 w-full"
              />
            </div>
            <label className="flex items-center gap-2 text-gray-400">
              <input
                type="checkbox"
                checked={expiredOnly}
                onChange={(e) => setExpiredOnly(e.target.checked)}
                className="form-checkbox text-green-500"
              />
              Apenas Vencidos
            </label>
            <div className="flex items-center">
              <ColumnSelector columns={COLUMNS} visibleKeys={visibleKeys} onToggle={toggleColumn} onReset={resetColumns} />
            </div>
          </div>

          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  {visibleColumns.map(col => (
                    <th key={col.key} className={col.headerClass || ''}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={visibleColumns.length} className="py-12 text-center">
                      <Loader2 className="animate-spin text-green-500 inline" size={28} />
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumns.length} className="py-12 text-center text-gray-500">
                      {selectedGroupId ? 'Nenhum documento encontrado neste grupo' : 'Selecione um grupo para ver os documentos'}
                    </td>
                  </tr>
                ) : (
                  filtered.map((doc) => (
                    <tr key={doc.id}>
                      {visibleColumns.map(col => (
                        <Fragment key={col.key}>{col.render(doc)}</Fragment>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Group Modal */}
      {showGroupModal && (
        <GroupModal
          group={editingGroup}
          onClose={() => { setShowGroupModal(false); setEditingGroup(null); }}
          onSave={editingGroup ? handleUpdateGroup : handleCreateGroup}
        />
      )}

      {/* Document Modal */}
      {showDocModal && (
        <DocumentModal
          document={editingDoc}
          groupId={selectedGroupId}
          sectors={sectors}
          onClose={() => { setShowDocModal(false); setEditingDoc(null); }}
          onSave={editingDoc ? handleUpdateDocument : handleCreateDocument}
        />
      )}

      {/* Versions Modal */}
      {versionsDoc && (
        <VersionsModal
          doc={versionsDoc}
          versions={versionsList}
          loading={versionsLoading}
          downloadingId={downloadingVersionId}
          formatBytes={formatBytes}
          onClose={() => { setVersionsDoc(null); setVersionsList([]); }}
          onDownload={handleDownloadVersion}
        />
      )}
    </div>
  );
};

const GroupModal = ({ group, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: group?.name || '',
    description: group?.description || '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({ ...formData, is_active: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-content p-6 max-w-lg">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">{group ? 'Editar Grupo' : 'Novo Grupo'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Nome *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input w-full"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Descrição</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input w-full"
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>Cancelar</button>
            <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
              {saving ? <Loader2 className="animate-spin" size={20} /> : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

const DocumentModal = ({ document, groupId, sectors, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: document?.name || '',
    edition: document?.edition || '',
    area_id: document?.area_id || '',
    storage_type: document?.storage_type || 'year',
    access_area_ids: document?.access_area_ids || [],
    retention_min: document?.retention_min || 'annual',
    disposal: document?.disposal || 'annual',
    expiration_date: document?.expiration_date || '',
  });

  const toggleAccessArea = (sectorId) => {
    setFormData((prev) => ({
      ...prev,
      access_area_ids: prev.access_area_ids.includes(sectorId)
        ? prev.access_area_ids.filter((id) => id !== sectorId)
        : [...prev.access_area_ids, sectorId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Informe o nome do documento');
      return;
    }
    const dateError = validateDateField(formData.expiration_date, 'Data de vencimento');
    if (dateError) { toast.error(dateError); return; }
    setSaving(true);
    try {
      const data = { ...formData };
      if (!data.area_id) delete data.area_id;
      if (!data.expiration_date) delete data.expiration_date;
      await onSave(data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-content p-6 max-w-2xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">{document ? 'Editar Documento' : 'Novo Documento'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Nome *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Edição</label>
              <input
                type="text"
                value={formData.edition}
                onChange={(e) => setFormData({ ...formData, edition: e.target.value })}
                className="input w-full"
                placeholder="Ex: 2ª"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Área</label>
              <select
                value={formData.area_id}
                onChange={(e) => setFormData({ ...formData, area_id: e.target.value })}
                className="input w-full"
              >
                <option value="">Selecione...</option>
                {sectors.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Armazenamento</label>
              <select
                value={formData.storage_type}
                onChange={(e) => setFormData({ ...formData, storage_type: e.target.value })}
                className="input w-full"
              >
                <option value="year">Por Ano</option>
                <option value="certificate">Por Certificado</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Retenção Mínima</label>
              <select
                value={formData.retention_min}
                onChange={(e) => setFormData({ ...formData, retention_min: e.target.value })}
                className="input w-full"
              >
                <option value="annual">Anual</option>
                <option value="biennial">Bienal</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Disposição</label>
              <select
                value={formData.disposal}
                onChange={(e) => setFormData({ ...formData, disposal: e.target.value })}
                className="input w-full"
              >
                <option value="annual">Anual</option>
                <option value="monthly">Mensal</option>
                <option value="semiannual">Semestral</option>
                <option value="quarterly">Trimestral</option>
                <option value="valid">Vigente</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Data de Vencimento</label>
              <input
                type="date"
                value={formData.expiration_date}
                onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
                className="input w-full"
                min="1900-01-01"
                max="2099-12-31"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Áreas que podem acessar</label>
            <p className="text-xs text-gray-500 mb-2">Selecione os setores que podem visualizar este documento. Nenhuma seleção = todos os setores.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-3 border border-gray-700 rounded-lg">
              {sectors.length === 0 ? (
                <p className="text-sm text-gray-500 col-span-full">Nenhum setor cadastrado</p>
              ) : (
                sectors.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 min-w-0 text-sm text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.access_area_ids.includes(s.id)}
                      onChange={() => toggleAccessArea(s.id)}
                      className="form-checkbox text-green-500 flex-shrink-0"
                    />
                    <span className="truncate min-w-0" title={s.name}>{s.name}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>Cancelar</button>
            <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
              {saving ? <Loader2 className="animate-spin" size={20} /> : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

const VersionsModal = ({ doc, versions, loading, downloadingId, formatBytes, onClose, onDownload }) => {
  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-content p-6 max-w-2xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <History className="text-green-500" />
            Histórico de Versões
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <p className="text-sm text-gray-400 mb-4">
          {doc.name}
          {doc.version && doc.version > 0 ? ` · Rev. ${doc.version}` : ''}
        </p>

        {loading ? (
          <div className="py-12 text-center">
            <Loader2 className="animate-spin text-green-500 inline" size={28} />
          </div>
        ) : versions.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">Nenhuma versão anexada</p>
        ) : (
          <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
            {versions.map((v) => (
              <div
                key={v.id}
                className="flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-700"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="px-1.5 py-0.5 rounded text-[10px] bg-green-500/20 text-green-400 font-medium">
                      Rev. {v.version}
                    </span>
                    {v.is_obsolete ? (
                      <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-700 text-gray-300">
                        Obsoleta
                      </span>
                    ) : (
                      <span className="px-1.5 py-0.5 rounded text-[10px] bg-blue-500/20 text-blue-400">
                        Vigente
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-white truncate mt-1">{v.file_name}</p>
                  <p className="text-xs text-gray-500">
                    {formatBytes(v.file_size)} · {formatDateBR(v.created_at)}
                    {v.uploaded_by_name ? ` · ${v.uploaded_by_name}` : ''}
                  </p>
                </div>
                <button
                  onClick={() => onDownload(v)}
                  disabled={downloadingId === v.id}
                  className="btn-secondary flex items-center gap-1 text-sm flex-shrink-0"
                  title="Baixar esta versão"
                >
                  {downloadingId === v.id ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Download size={14} />
                  )}
                  Baixar
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default ExternalDocuments;
